import { useGetFaireAcademyPageState } from "@faire/deprecated-retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { useGetStoreType } from "@faire/deprecated-retailer-visitor-shared/hooks/openWithFaire/useGetStoreType";
import { useNationality } from "@faire/deprecated-retailer-visitor-shared/hooks/retailer/useNationality";
import { getSettingOpenWithFaireTotalApplicationCount } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingOpenWithFaireTotalApplicationCount";
import { getSettingOpenWithFaireTotalPaymentTermsGranted } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingOpenWithFaireTotalPaymentTermsGranted";
import {
  FAOuterWrapper,
  FAInnerWrapperRow,
} from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/components/styles";
import { OWF_SECTION_NAME } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/types";
import { Core, Decorative } from "@faire/design-tokens";
import { trackOpenWithFaireFaireAcademySectionView } from "@faire/web-api--source/events/openWithFaire/view/faireAcademySection";
import { localNum } from "@faire/web/common/localization";
import { formatMoney, RoundingStrategy } from "@faire/web/common/money";
import { StoreType } from "@faire/web/common/StoreType";
import { Flex, Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { SlideIn } from "@faire/web/ui/SlideIn/SlideIn";
import React from "react";
import styled from "styled-components";

const CIRCLE_AVATAR_IMAGES: string[] = [
  "https://cdn.faire.com/fastly/575248c61bc4245c7ca222885ed619c46926eaaff19ca65949e13c2ac453c369.png",
  "https://cdn.faire.com/fastly/94b29604fc3f9652b1865a9d5ebe77a5c6e18c9c37343db2a3fef37097b5a435.png",
  "https://cdn.faire.com/fastly/7845f7d95692270c0a944375d37c0312ede68b55d75e6dbc4d3070e93f8cad5d.png",
];

export const UpsellStats: React.FC = () => {
  const { pageState } = useGetFaireAcademyPageState();
  const ref = useDoActionWhenVisible(() =>
    trackOpenWithFaireFaireAcademySectionView(
      pageState,
      OWF_SECTION_NAME.UPSELL_STATS
    )
  );

  return (
    <FAOuterWrapper ref={ref} $bgColor={Decorative.tint.neutral}>
      <FAInnerWrapperRow justify="space-between">
        <SlideIn offset="50px">
          <UpsellStatsLeftCard />
        </SlideIn>
        <Spacer size="4x" />
        <SlideIn offset="50px">
          <UpsellStatsImage />
        </SlideIn>
      </FAInnerWrapperRow>
    </FAOuterWrapper>
  );
};

const UpsellStatsImage: React.FC = () => {
  const { storeType, shouldShowPartialRetailersBasedOnStoreType } =
    useGetStoreType();
  let imageSrc =
    "https://cdn.faire.com/fastly/d46e65c03d3329af9da1f835f3efd663de57bfef4b65ee5e27c43d24160a5b20.png";

  if (shouldShowPartialRetailersBasedOnStoreType) {
    if (storeType === StoreType.SPA_OR_SALON) {
      imageSrc =
        "https://cdn.faire.com/fastly/b8b1f534962fb83c72d54c3e4dda42b1fee8f7dd1dcb2da427567f2321964303.png";
    } else if (storeType === StoreType.CAFE_OR_RESTAURANT) {
      imageSrc =
        "https://cdn.faire.com/fastly/bc35cf633b7612fce97f2e59f774894ad099bc540cc7f6144b7f9f3fad26f77e.png";
    }
  }

  return <StyledImage src={imageSrc} alt="" />;
};

const UpsellStatsHeaderText: React.FC = () => {
  const { storeType, shouldShowPartialRetailersBasedOnStoreType } =
    useGetStoreType();

  if (!shouldShowPartialRetailersBasedOnStoreType) {
    return (
      <StrictLocalMsg
        id="maDQh5"
        defaultMessage="New store owners love Open with Faire"
        description={{
          text: "Header title for Open with Faire program upsell statistics section",
          img: "https://cdn.faire.com/fastly/7c92c6624ccffe3dd0f50628cd67b42a608d8a6a6d256fc123f658dad6d9709e.png",
        }}
      />
    );
  }

  switch (storeType) {
    case StoreType.SPA_OR_SALON:
      return (
        <StrictLocalMsg
          id="pXoSVw"
          defaultMessage="Spas and salons love Open with Faire"
          description={{
            text: "Header title for Open with Faire program upsell statistics section",
            img: "https://cdn.faire.com/fastly/6b23d75c283b4366352b1d9b53d01ebfeaa44b8840c909109c49fc41c761b1ff.png",
          }}
        />
      );
    case StoreType.CAFE_OR_RESTAURANT:
      return (
        <StrictLocalMsg
          id="wn+8Cf"
          defaultMessage="Cafes and restaurants love Open with Faire"
          description={{
            text: "Header title for Open with Faire program upsell statistics section",
            img: "https://cdn.faire.com/fastly/0ff5c342ead995811b42d75d00da12a419fe544637dd4d8541c0dc1a773e8e43.png",
          }}
        />
      );
    default:
      return (
        <StrictLocalMsg
          id="maDQh5"
          defaultMessage="New store owners love Open with Faire"
          description={{
            text: "Header title for Open with Faire program upsell statistics section",
            img: "https://cdn.faire.com/fastly/7c92c6624ccffe3dd0f50628cd67b42a608d8a6a6d256fc123f658dad6d9709e.png",
          }}
        />
      );
  }
};

const UpsellStatsLeftCard: React.FC = () => {
  return (
    <Column justify="center">
      <CircleAvatars />
      <Spacer
        height={{
          mobileAndAbove: 12,
          desktopAndAbove: "6x",
        }}
      />
      <SlideIn offset="50px">
        <Typography
          style={{ maxWidth: "360px" }}
          align="left"
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            desktopAndAbove: "displayMSerifRegular",
          }}
        >
          <UpsellStatsHeaderText />
        </Typography>
      </SlideIn>
      <Spacer
        height={{
          mobileAndAbove: "3x",
          desktopAndAbove: "5x",
        }}
      />
      <CurrentOwfStats />
    </Column>
  );
};

const CircleAvatars = () => {
  return (
    <Row>
      {CIRCLE_AVATAR_IMAGES.map((image: string, index: number) => (
        <CircleImage key={image} src={image} $isNotFirst={index !== 0} alt="" />
      ))}
    </Row>
  );
};

const CurrentOwfStats: React.FC = () => {
  const { isEurope } = useNationality();
  const moneyFigure = getSettingOpenWithFaireTotalPaymentTermsGranted();

  const formattedProvidedPaymentTerms = formatMoney({
    money: moneyFigure,
    compact: true,
    roundingStrategy:
      (moneyFigure.amount_cents ?? 0 > 100000000 * 100)
        ? RoundingStrategy.ROUND_HUNDREDS_DOWN
        : RoundingStrategy.NONE,
  });
  const providedPaymentTermsFigureNumber = isEurope
    ? `+${formattedProvidedPaymentTerms}`
    : `${formattedProvidedPaymentTerms}+`;

  const totalApplicationNumberBase = localNum(
    getSettingOpenWithFaireTotalApplicationCount()
  );
  const totalApplicationNumber = isEurope
    ? `+${totalApplicationNumberBase}`
    : `${totalApplicationNumberBase}+`;

  return (
    <CurrentOwfStatsWrapper>
      <StatRenderer
        figureNumber={totalApplicationNumber}
        figureText={
          <StrictLocalMsg
            id="IX7TIh"
            defaultMessage="Using Open with Faire"
            description={{
              text: "Subheader title associated with a figure number, showing how many customers are 'Using Open with Faire'",
              img: "https://cdn.faire.com/fastly/7c92c6624ccffe3dd0f50628cd67b42a608d8a6a6d256fc123f658dad6d9709e.png",
            }}
          />
        }
      />
      <Spacer
        size={{
          mobileAndAbove: "2x",
          desktopAndAbove: "6x",
        }}
      />
      <StatRenderer
        figureNumber={providedPaymentTermsFigureNumber}
        figureText={
          <StrictLocalMsg
            id="lSySZX"
            defaultMessage="Payment terms provided"
            description={{
              text: "Subheader title associated with a figure number, showing how much 'Payment terms [money] provided'",
              img: "https://cdn.faire.com/fastly/7c92c6624ccffe3dd0f50628cd67b42a608d8a6a6d256fc123f658dad6d9709e.png",
            }}
          />
        }
      />
    </CurrentOwfStatsWrapper>
  );
};

const StatRenderer: React.FC<{
  figureNumber: string;
  figureText: React.ReactNode;
}> = ({ figureNumber, figureText }) => {
  return (
    <Column>
      <Typography align="left" variant="displayLSerifRegular">
        {figureNumber}
      </Typography>
      <Spacer
        height={{
          mobileAndAbove: 4,
          desktopAndAbove: "1x",
        }}
      />
      <Typography
        align="left"
        variant={{
          mobileAndAbove: "paragraphSansRegular",
          desktopAndAbove: "subheadingSansRegular",
        }}
      >
        {figureText}
      </Typography>
    </Column>
  );
};

const CurrentOwfStatsWrapper = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", tabletAndAbove: "row" },
})``;

const CircleImage = styled.img<{ $isNotFirst?: boolean }>`
  width: ${getSpacing("6x")};
  height: ${getSpacing("6x")};
  border-radius: 50%;
  border: 1px solid ${Core.surface.inverse};
  margin-left: ${(props) => (props.$isNotFirst ? "-10px" : "")};
`;

const StyledImage = styled.img`
  width: 425px;
  height: 425px;

  ${responsiveMediaQueries.tablet} {
    width: 300px;
    height: 300px;
  }
  ${responsiveMediaQueries.mobile} {
    max-width: 377px;
    max-height: 377px;
    width: 100%;
    height: 100%;
  }
`;
